import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, HeartIcon, ArrowRightIcon, TvIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
const DONATE_URL = 'https://givebutter.com/honduran-american-association-campaign';
const STORAGE_KEY = 'haa-founding-funders-dismissed';
const VIDEO_EMBED = 'https://www.youtube.com/embed/FHT_E5aUqJc?start=379&autoplay=1&mute=1&rel=0&playsinline=1&modestbranding=1&enablejsapi=1';
const VIDEO_VOLUME = 35;
export function FoundingFundersPopup() {
  const {
    language
  } = useLanguage();
  const isEs = language === 'ES';
  const [isOpen, setIsOpen] = useState(false);
  const frameRef = useRef<HTMLIFrameElement | null>(null);
  const command = (func: string, args: unknown[] = []) => {
    frameRef.current?.contentWindow?.postMessage(JSON.stringify({
      event: 'command',
      func,
      args
    }), '*');
  };
  const handleVideoReady = () => {
    // Autoplay requires a muted start; unmute and settle at a gentle
    // below-midpoint volume once playback has begun.
    command('playVideo');
    command('setVolume', [VIDEO_VOLUME]);
    setTimeout(() => {
      command('unMute');
      command('setVolume', [VIDEO_VOLUME]);
    }, 700);
  };
  useEffect(() => {
    try {
      if (sessionStorage.getItem(STORAGE_KEY)) return;
    } catch {}
    const timer = setTimeout(() => setIsOpen(true), 1200);
    return () => clearTimeout(timer);
  }, []);
  const handleClose = () => {
    setIsOpen(false);
    try {
      sessionStorage.setItem(STORAGE_KEY, '1');
    } catch {}
  };
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen]);
  return <AnimatePresence data-id="element-2435" data-mp="7vv822etzjrw">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.25
    }} className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={handleClose} role="dialog" aria-modal="true" aria-label={isEs ? 'Invitación a ver la entrevista' : 'Invitation to watch the interview'} data-id="element-2436" data-mp="7v1zcvwbak74">
          <motion.div initial={{
        opacity: 0,
        scale: 0.94,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.94,
        y: 20
      }} transition={{
        duration: 0.35,
        ease: [0.16, 1, 0.3, 1]
      }} className="relative w-full max-w-lg rounded-2xl overflow-hidden shadow-2xl bg-haa-navy text-white" onClick={e => e.stopPropagation()} data-id="element-2437" data-mp="2h75p5xvuwk4p">
            {/* Flag stripe */}
            <div className="h-1 w-full flex" data-id="element-2438" data-mp="1orqub106rpoj">
              <div className="flex-1 bg-haa-blue" data-id="element-2439" data-mp="1jruqb1rhny8z" />
              <div className="flex-1 bg-white" data-id="element-2440" data-mp="1jr38quuq811p" />
              <div className="flex-1 bg-haa-blue" data-id="element-2441" data-mp="1xrmbbmgfaewh" />
            </div>

            <button onClick={handleClose} className="absolute top-3 right-3 z-10 p-1.5 rounded-full bg-black/50 hover:bg-black/70 text-white transition-colors" aria-label={isEs ? 'Cerrar' : 'Close'} data-id="element-2442" data-mp="1xpt68blgk2ql">
              <XIcon className="w-4 h-4" data-id="element-2443" data-mp="22i2qpc3t0alh" />
            </button>

            {/* Video */}
            <div className="relative w-full aspect-video overflow-hidden bg-black" data-id="element-2444" data-mp="2hf3jzk1n8dm2">
              <iframe ref={frameRef} onLoad={handleVideoReady} src={VIDEO_EMBED} title={isEs ? 'Entrevista al Presidente de la Asociación Hondureño-Americana' : 'Interview with the Honduran-American Association President'} className="absolute inset-0 w-full h-full border-0" allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen data-id="element-2445" data-mp="2cgs8l4vp9v1q" />
            </div>

            <div className="p-6 sm:p-7" data-id="element-2446" data-mp="3n27wqok4s2tw">
              <p className="inline-flex items-center gap-2 font-nunito text-amber-400 text-[10px] font-bold uppercase tracking-[0.25em] mb-2.5" data-id="element-2447" data-mp="3ryy84ykzqa8z">
                <TvIcon className="w-3.5 h-3.5" data-id="element-2448" data-mp="rltorbhsawr" />
                {isEs ? 'No se lo pierda' : "Don't miss it"}
              </p>

              <h2 className="font-bebas text-2xl sm:text-3xl tracking-wider leading-tight mb-3" data-id="element-2449" data-mp="5px3j21selez">
                {isEs ? 'Conozca Nuestras Herramientas Consulares' : 'Learn About Our Consular Tools'}
              </h2>

              <div className="w-12 h-px bg-amber-400 mb-4" data-id="element-2450" data-mp="31o5zn9lkzhn6" />

              <p className="font-nunito text-white/85 text-sm leading-relaxed mb-5" data-id="element-2451" data-mp="2wqmgpwdxugow">
                {isEs ? 'Nuestro Presidente conversa sobre inmigración y lo que está en juego para la comunidad hondureño-americana.' : 'Our President discusses immigration and what is at stake for the Honduran-American community.'}
              </p>

              <div className="flex flex-col gap-2" data-id="element-2452" data-mp="2hz5aplepf7vt">
                <a href={DONATE_URL} target="_blank" rel="noopener noreferrer" onClick={handleClose} className="w-full inline-flex items-center justify-center gap-2 bg-amber-400 hover:bg-amber-300 text-haa-navy font-nunito font-bold text-xs uppercase tracking-wider px-5 py-3 rounded-xl transition-colors" data-id="element-2453" data-mp="2dytow5alv1ir">
                  <HeartIcon className="w-4 h-4" data-id="element-2454" data-mp="2aviv5zqo2o5" />
                  {isEs ? 'Apoye Nuestra Misión' : 'Support Our Mission'}
                  <ArrowRightIcon className="w-4 h-4" data-id="element-2455" data-mp="1wohjvprxvg1t" />
                </a>
                <button onClick={handleClose} className="w-full inline-flex items-center justify-center gap-2 bg-haa-skyblue hover:bg-white text-haa-navy font-nunito font-bold text-xs uppercase tracking-wider px-5 py-2.5 rounded-xl transition-colors" data-id="element-2456" data-mp="21iu07qy4cb9c">
                  {isEs ? 'Llévame a la Página Principal' : 'Take Me to the Homepage'}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}